import { useRef, useState } from "react";
import { Mic, Square, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

// Speak & Log — records a short audio clip, transcribes it via Whisper (/voice/transcribe),
// and hands the text back through onTranscript. Reusable across chat + tracker inputs.
export const VoiceButton = ({ onTranscript, disabled = false, className = "", testId = "voice-btn" }) => {
  const [state, setState] = useState("idle"); // idle | recording | transcribing
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);

  const stopRecording = () => {
    if (mediaRef.current && mediaRef.current.state !== "inactive") mediaRef.current.stop();
  };

  const start = async () => {
    if (state === "recording") return stopRecording();
    if (state === "transcribing") return;
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      toast.error("Voice input isn't supported on this device");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported("audio/webm")
        ? "audio/webm"
        : MediaRecorder.isTypeSupported("audio/mp4")
        ? "audio/mp4"
        : "";
      const mr = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        streamRef.current?.getTracks().forEach((t) => t.stop());
        const type = mr.mimeType || "audio/webm";
        const blob = new Blob(chunksRef.current, { type });
        if (!blob.size) { setState("idle"); return; }
        setState("transcribing");
        try {
          const ext = type.includes("mp4") ? "m4a" : "webm";
          const fd = new FormData();
          fd.append("file", blob, `voice.${ext}`);
          const { data } = await api.post("/voice/transcribe", fd);
          const text = (data?.text || "").trim();
          if (text) onTranscript?.(text);
          else toast.info("Didn't catch that — please try again");
        } catch (err) {
          toast.error("Couldn't transcribe audio — please try again");
        } finally {
          setState("idle");
        }
      };
      mr.start(250);
      mediaRef.current = mr;
      setState("recording");
    } catch (e) {
      const name = e?.name || "";
      const isNativeApp = typeof navigator !== "undefined" && /KAAYAApp/.test(navigator.userAgent || "");
      if (!window.isSecureContext) {
        toast.error("Mic needs a secure (https) page to work");
      } else if (name === "NotAllowedError" || name === "SecurityError") {
        toast.error(
          isNativeApp
            ? "Microphone is off for KAAYA — open your phone's Settings › Apps › KAAYA › Permissions, enable Microphone, then reopen the app"
            : window.self !== window.top
              ? "Mic is blocked inside the embedded preview — open the app in its own browser tab"
              : "Microphone permission blocked — allow mic access in your browser/site settings"
        );
      } else if (name === "NotFoundError" || name === "OverconstrainedError") {
        toast.error("No microphone found on this device");
      } else {
        toast.error("Couldn't start the mic — please try again");
      }
      console.error("VoiceButton getUserMedia error:", e);
      setState("idle");
    }
  };

  const recording = state === "recording";
  const busy = state === "transcribing";

  return (
    <button
      type="button"
      onClick={start}
      disabled={disabled || busy}
      data-testid={testId}
      aria-label={recording ? "Stop recording" : busy ? "Transcribing" : "Speak"}
      title={recording ? "Stop" : busy ? "Transcribing…" : "Speak to log"}
      className={`h-10 w-10 shrink-0 flex items-center justify-center rounded-full transition active:scale-95 disabled:opacity-40 ${
        recording
          ? "bg-destructive text-destructive-foreground animate-pulse"
          : "text-secondary hover:bg-muted"
      } ${className}`}
    >
      {busy ? <Loader2 size={20} className="animate-spin" /> : recording ? <Square size={16} className="fill-current" /> : <Mic size={20} />}
    </button>
  );
};

export default VoiceButton;
